/**
 * Conditional element overrides — one element that looks different in different
 * states, instead of two elements that duplicate each other.
 *
 * The model had seven places where the same field was declared twice, differing
 * only by a `condition` and one or two properties: an editable input on create
 * and a read-only display on edit, or a different control per document type.
 * Every one of those pairs duplicated four to twelve identical properties, and
 * because both halves carry the same `id` — the id IS the form binding — the
 * array could never be keyed, so a customer overlay could not address them.
 *
 *   {
 *       id: 'date', type: 'date', label: '…', required: true,
 *       conditionals: [
 *           { condition: { field: '$mode', operator: 'equals', value: 'edit' },
 *             set:   { type: 'text-readOnly', displayType: 'date' },
 *             unset: ['linkedFields'] }
 *       ]
 *   }
 *
 * `condition` on the element decides WHETHER it renders. `conditionals` decides
 * WHAT IT LOOKS LIKE when it does. They compose and neither implies the other.
 *
 * ## Why `unset` rather than `null` meaning delete
 *
 * The patch format (30.03.02 §4.1) follows RFC 7386, where `null` deletes a key.
 * This deliberately does not, because the two have different authors. A patch
 * targets the model from outside; `conditionals` is written inside the model by
 * the same people who write `icon: null` 473 times to mean "no icon here".
 * Adopting null-as-delete would make the model contradict itself, so removal is
 * explicit and `null` keeps the meaning it already has everywhere else.
 */

import { evaluateCondition } from './evaluate.js'

/**
 * Apply every matching conditional to an element, in order, last write winning.
 *
 * Returns the element BY REFERENCE when nothing matched, so a renderer that
 * memoises on identity sees no change — the same property the patch engine's
 * empty-patch case has.
 */
export function resolveElement(item, sources) {
    if (!item || typeof item !== 'object') return item
    const conditionals = item.conditionals
    if (!Array.isArray(conditionals) || conditionals.length === 0) return item

    let out = item
    for (const rule of conditionals) {
        if (!rule || typeof rule !== 'object') continue
        if (!evaluateCondition(rule.condition, sources)) continue

        if (out === item) out = { ...item }   // copy on first match only
        if (rule.set && typeof rule.set === 'object') Object.assign(out, rule.set)
        for (const key of rule.unset ?? []) delete out[key]
    }

    // The resolved element is what renderers consume; the recipe is not part of it.
    if (out !== item) delete out.conditionals
    return out
}

/**
 * Every field the element's conditionals read, so a component can subscribe to
 * exactly those alongside the fields its own `condition` reads.
 */
export function conditionalFieldsOf(item) {
    const out = []
    for (const rule of item?.conditionals ?? []) {
        const visit = (c) => {
            if (!c || typeof c !== 'object') return
            if (Array.isArray(c)) { c.forEach(visit); return }
            if (Array.isArray(c.all)) c.all.forEach(visit)
            if (Array.isArray(c.any)) c.any.forEach(visit)
            if (typeof c.field === 'string' && c.field && c.field.charCodeAt(0) !== 36) {
                if (!out.includes(c.field)) out.push(c.field)
            }
        }
        visit(rule?.condition)
    }
    return out
}
